import posthog from "posthog-js"

import { analyticsOptedOut, analyticsPagePath, setPosthogMirror } from "@/lib/analytics"
import { cleanPosthogEvent, identityAction, RECORDING_MASK_TEXT_SELECTOR } from "@/lib/posthog-privacy"
import type { SharedProps } from "@/types"

// Browser PostHog: page views, session recordings, autocapture and a few
// mirrored tool and funnel events. Server events come from ProductAnalytics
// with the same user UUID as distinct_id, so both streams join.
//
// Elements with the `ph-no-capture` class (birth details form, tool results,
// the login email) are left out of recordings and autocapture entirely.
let started = false
let maskedFor: boolean | null = null
let identifiedId: string | null = null
let lastPageviewKey: string | null = null
let environment: string | null = null

type PageProps = Pick<SharedProps, "auth" | "posthog" | "signed_in">

function applyMasking(signedIn: boolean) {
  document.documentElement.toggleAttribute("data-ph-signed-in", signedIn)
  if (maskedFor === signedIn) return
  posthog.set_config({ mask_all_text: signedIn, mask_all_element_attributes: signedIn })
  maskedFor = signedIn
}

function optOutIfNeeded() {
  if (!analyticsOptedOut()) return false
  posthog.opt_out_capturing()
  return true
}

// Inertia "beforeUpdate": runs before the next page renders, so masking (and
// an opt-out set by a fresh sign-in) is in place before its DOM is recorded.
// It only ever turns masking on; switching it off waits for "navigate", once
// the signed-in page is gone ("mask early, unmask late").
export function preparePosthogPage(props: PageProps) {
  if (!started || optOutIfNeeded()) return
  if (props.signed_in) applyMasking(true)
}

// Back/Forward restores a page from history without "beforeUpdate", so mask
// on every popstate until "navigate" knows which page it was.
// An in-page anchor jump fires popstate with no state: same page, no change.
export function preparePosthogHistoryStep(event: PopStateEvent) {
  if (!event.state || !started || optOutIfNeeded()) return
  applyMasking(true)
}

function syncIdentity(props: PageProps) {
  const userId = props.auth?.user?.id ?? null
  const action = identityAction({ identifiedId }, { userId, signedIn: Boolean(props.signed_in) })
  if (action === "identify" && userId) {
    posthog.identify(userId)
    identifiedId = userId
  } else if (action === "reset") {
    posthog.reset()
    // reset() drops registered super properties too.
    posthog.register({ environment: environment ?? "" })
    identifiedId = null
  }
}

function capturePageview() {
  const key = `${window.location.pathname}${window.location.search}`
  if (key === lastPageviewKey) return
  lastPageviewKey = key
  posthog.capture("$pageview")
}

function start(config: NonNullable<SharedProps["posthog"]>, signedInPage: boolean) {
  // A Back/Forward document load may restore a signed-in page from history
  // whatever the server props say, so it starts masked ("mask early").
  const historyRestore =
    (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type === "back_forward" &&
    Boolean(window.history.state?.page)
  const signedIn = signedInPage || historyRestore
  environment = config.environment
  // Set before init so the first full snapshot is already masked.
  document.documentElement.toggleAttribute("data-ph-signed-in", signedIn)
  posthog.init(config.token, {
    api_host: config.host,
    ui_host: config.ui_host,
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    person_profiles: "always",
    mask_all_text: signedIn,
    mask_all_element_attributes: signedIn,
    session_recording: { maskAllInputs: true, maskTextSelector: RECORDING_MASK_TEXT_SELECTOR },
    before_send: (event) => cleanPosthogEvent(event, analyticsPagePath, window.location.origin),
  })
  posthog.register({ environment: config.environment })
  identifiedId = posthog.get_property("$user_state") === "identified" ? posthog.get_distinct_id() : null
  maskedFor = signedIn
  started = true
  setPosthogMirror(capturePosthogEvent)
}

// Called once at boot with the first page, then on every Inertia navigation.
export function syncPosthogPage(props: PageProps) {
  if (!started) {
    if (!props.posthog || analyticsOptedOut()) return
    start(props.posthog, Boolean(props.signed_in))
  }

  if (optOutIfNeeded()) return

  applyMasking(Boolean(props.signed_in))
  syncIdentity(props)
  capturePageview()
}

function capturePosthogEvent(name: string, properties: Record<string, unknown>) {
  posthog.capture(name, properties)
}
